import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AddressBlock, EMPTY_ADDRESS, MobileField, mobileError } from "@/components/SeaAddress";
import { MultiTagSelect } from "@/components/MultiTagSelect";
import { OTHER, isOther } from "@/lib/ranks";
import { toast } from "sonner";

const EMPTY_NOK = { name: "", relation: "", mobile: "", address: EMPTY_ADDRESS };
const EMPTY = { full_name: "", ranks: [], vessel_types: [], cdc_number: "", passport_number: "", passport_expiry: "", availability_status: "Available", phone: "", email: "", address: EMPTY_ADDRESS, nok: EMPTY_NOK };
const FIELDS = [["full_name", "Full name"], ["cdc_number", "CDC number"], ["passport_number", "Passport number"], ["email", "Email"]];

export function CandidateForm({ open, onOpenChange, meta, candidate, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [otherRank, setOtherRank] = useState("");

  useEffect(() => {
    if (!open) return;
    setPhoto(null);
    setOtherRank("");
    setForm(candidate ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, candidate[k] ?? EMPTY[k]])), address: { ...EMPTY_ADDRESS, ...(candidate.address || {}) },
      nok: { ...EMPTY_NOK, ...(candidate.nok || {}), address: { ...EMPTY_ADDRESS, ...(candidate.nok?.address || {}) } } } : EMPTY);
  }, [open, candidate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.ranks.length) { toast.error("Full name and at least one rank are required"); return; }
    if (form.ranks.includes(OTHER) && otherRank.trim().length < 2) { toast.error("Type the custom rank name for Others"); return; }
    const bad = [[form.phone, "Mobile"], [form.nok.mobile, "NOK mobile"]].find(([m]) => m?.split(" ")[1] && mobileError(...m.split(" ")));
    if (bad) { toast.error(`${bad[1]} must be 10 digits`); return; }
    setSaving(true);
    try {
      const ranks = form.ranks.map((r) => (r === OTHER ? `${OTHER}: ${otherRank.trim()}` : r));
      const body = { ...form, ranks, passport_expiry: form.passport_expiry || null };
      const id = candidate ? (await api.put(`/candidates/${candidate.id}`, body), candidate.id) : (await api.post("/candidates", body)).data.id;
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        await api.post(`/candidates/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } })
          .catch((err) => toast.warning(`Candidate saved, but the photo failed: ${formatApiError(err)}`));
      }
      toast.success(candidate ? "Candidate updated" : "Candidate added");
      onOpenChange(false);
      onSaved(id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="candidate-form-dialog">
        <DialogHeader>
          <DialogTitle>{candidate ? "Edit candidate" : "Add candidate"}</DialogTitle>
          <DialogDescription>Source is set to you automatically.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`cand-${k}`}>{l}</Label>
              <Input id={`cand-${k}`} value={form[k]} onChange={set(k)} data-testid={`candidate-${k}-input`} />
            </div>
          ))}
          <MobileField label="Mobile" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} prefix="candidate-phone" />
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Ranks</Label>
            <MultiTagSelect options={[...(meta?.ranks || []), ...form.ranks.filter((r) => isOther(r) && r !== OTHER)]} value={form.ranks} onChange={(v) => setForm({ ...form, ranks: v })} placeholder="Select one or more ranks" testid="candidate-rank-select" />
            {form.ranks.includes(OTHER) && (
              <Input value={otherRank} onChange={(e) => setOtherRank(e.target.value)} placeholder="Type the custom rank name for Others" data-testid="candidate-rank-other-input" />
            )}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Type of vessel sailed on</Label>
            <MultiTagSelect options={meta?.vessel_types || []} value={form.vessel_types} onChange={(v) => setForm({ ...form, vessel_types: v })} placeholder="Select vessel types" testid="candidate-vessel-types-select" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cand-pexp">Passport expiry</Label>
            <Input id="cand-pexp" type="date" value={form.passport_expiry || ""} onChange={set("passport_expiry")} data-testid="candidate-passport_expiry-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Availability</Label>
            <Select value={form.availability_status} onValueChange={(v) => setForm({ ...form, availability_status: v })}>
              <SelectTrigger data-testid="candidate-availability-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.availability || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cand-photo">Photo (JPG/PNG)</Label>
            <Input id="cand-photo" type="file" accept=".jpg,.jpeg,.png" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="candidate-photo-input" />
          </div>
          <AddressBlock title="Address" value={form.address} onChange={(a) => setForm({ ...form, address: a })} prefix="candidate-address" />
          <div className="sm:col-span-2 text-sm font-semibold text-slate-800 pt-2 border-t border-slate-100">Next of kin</div>
          {[["name", "NOK name"], ["relation", "Relation"]].map(([k, l]) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`cand-nok-${k}`}>{l}</Label>
              <Input id={`cand-nok-${k}`} value={form.nok[k]} onChange={(e) => setForm({ ...form, nok: { ...form.nok, [k]: e.target.value } })} data-testid={`candidate-nok-${k}-input`} />
            </div>
          ))}
          <MobileField label="NOK mobile" value={form.nok.mobile} onChange={(v) => setForm({ ...form, nok: { ...form.nok, mobile: v } })} prefix="candidate-nok-mobile" />
          <div />
          <AddressBlock title="NOK address" value={form.nok.address} onChange={(a) => setForm({ ...form, nok: { ...form.nok, address: a } })} prefix="candidate-nok-address" />
          <DialogFooter className="sm:col-span-2">
            <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="candidate-form-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : candidate ? "Save changes" : "Add candidate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
